import axios from "axios";
import { Base64 } from "js-base64";
import Compressor from "compressorjs";
import debounce from "lodash/debounce";
import { GetUTCTime } from "./GetUTCTime";
import { origin } from "../Config/Api-constants";
import { resolveCommentRatings, resolveLineRatings, resolveRemovedDefaultRatingId, isTruthyFlag } from "./LineRatingUtils";
import { v4 as uuidv4 } from "uuid";

//WRW URL
const BASE_URL = await origin();

// Tracks the last progress actually sent to inspection-progress, so an unchanged calculation
// (e.g. re-renders recomputing the same counts) doesn't schedule a redundant POST.
let lastSentProgress = null;

const debouncedSendResult = debounce(async (result) => {
  const wasSuccessful = await updateProgressData(result);
  if (wasSuccessful) {
    lastSentProgress = { overallLinesPresent: result.overallLinesPresent, overallLinesCompleted: result.overallLinesCompleted };
  }
}, 10000);


const updateProgressData = async (data) => {
  const apiKey = sessionStorage.getItem("CK");
  const secretKey = sessionStorage.getItem("SK");
  const inspGuid = sessionStorage.getItem("guid");


  if (!apiKey || !secretKey || !inspGuid) {
    console.warn("Missing required sessionStorage keys");
    return false;
  }

  try {
    const url = `${BASE_URL}inspection-progress`;

    const payload = {
      inspection_guid: inspGuid,
      incomplete_line_input_controls: data?.overallLinesPresent,
      completed_line_input_controls: data?.overallLinesCompleted,
    }
    // Add GUID or other metadata to the payload if needed
    const finalPayload = {
      // guid: inspGuid,
      ...payload
    };

    const response = await axios.post(url, finalPayload, {
      headers: {
        "api-key": Base64.decode(apiKey),
        "Authorization": `Bearer ${Base64.decode(secretKey)}`,
        "Content-Type": "application/json",
        "platform": 'web',
        "api-platform": 'WRW',
        "user_id": sessionStorage.getItem('UID'),
        "product": sessionStorage.getItem('asoCom'),
        "mode": 'online',
      }
    });

    const apiData = response.data?.data;
    if (apiData?.status === "success") {
      console.info("Progress successfully updated");
      return true;
    }
    console.warn("Unexpected API response:", apiData);
    return false;
  } catch (err) {
    console.error("Progress update failed:", err);
    return false;
  }
};

export const totalLineCountCalulate = (inspTemplates) => {
  let overallLinesPresent = 0;
  let overallLinesCompleted = 0;
  let overallPrecent = 0;
  inspTemplates?.filter((itm) => itm?.is_deleted !== 1
  )?.forEach((template, index) => {
    template?.categories?.filter((itm) => itm?.is_deleted !== 1
    )?.forEach((category) => {
      if (index !== 0 && category.is_inspection_details === 1) {
        overallLinesPresent += 0;
        overallLinesCompleted += 0;
      } else {
        let { lineComplete, totalLines } = categoryPercentCalculate(category);
        overallLinesPresent += totalLines;
        overallLinesCompleted += lineComplete;
      }
    });
  });
  overallPrecent = (overallLinesCompleted / overallLinesPresent) * 100;
  const result = { overallLinesPresent, overallLinesCompleted, overallPrecent };

  // Nothing to report until the template tree actually has line input controls to count, and
  // there is no point posting progress for an inspection session that isn't set up yet.
  const canCalculateProgress = overallLinesPresent > 0;
  const hasProgressChanged = lastSentProgress?.overallLinesPresent !== overallLinesPresent || lastSentProgress?.overallLinesCompleted !== overallLinesCompleted;
  if (canCalculateProgress && sessionStorage.getItem("guid") && hasProgressChanged) {
    // Update progress in BE - lastSentProgress is committed inside debouncedSendResult, only
    // after a confirmed success response.
    debouncedSendResult(result);
  }
  return { overallLinesPresent, overallLinesCompleted, overallPrecent };
};

export const totalLineCountCalulateForReview = (inspTemplates) => {
  let overallLinesPresent = 0;
  let overallLinesCompleted = 0;
  let overallPrecent = 0;
  inspTemplates?.filter((itm) => itm?.is_deleted !== 1
  )?.forEach((template) => {
    template?.categories?.filter((itm) => itm?.is_deleted !== 1
    )?.forEach((category) => {
      if (category.is_inspection_details === 1) {
        overallLinesPresent += 0;
        overallLinesCompleted += 0;
      } else {
        let { lineComplete, totalLines } = categoryPercentCalculate(category);
        overallLinesPresent += totalLines;
        overallLinesCompleted += lineComplete;
      }
    });
  });
  overallPrecent = (overallLinesCompleted / overallLinesPresent) * 100;
  return { overallLinesPresent, overallLinesCompleted, overallPrecent };
};

export const categoryPercentCalculateNarrative = (category) => {
  if (category?.lines === null || category?.lines?.length === 0) return { percentage: 0, lineComplete: 0, totalLines: 0 };
  let totalLines = 0;
  let lineComplete = 0;
  category.lines
    ?.filter((itm) => itm?.is_deleted !== 1)
    .forEach((line) => {
      totalLines = totalLines + 1;
      if (line?.is_completed === true) {
        lineComplete = lineComplete + 1;
      }
    });
  const percentage = totalLines > 0 ? (lineComplete / totalLines) * 100 : 0;
  return { percentage, lineComplete, totalLines };
};

export const categoryPercentCalculate = (category) => {
  if (category?.lines === null || category?.lines?.length === 0) return { percentage: 0, lineComplete: 0, totalLines: 0 };
  else {
    let totalLines = 0;
    let lineComplete = 0;
    let percentage = 0;
    category.lines
      ?.filter(
        (itm) => itm?.is_deleted !== 1
        // && itm.optional !==1
      )
      .forEach((line) => {
        var isLineInputIncomplete = false;
        totalLines = totalLines + 1;
        if (line?.is_completed === true) {
          lineComplete = lineComplete + 1;
          return; // Skip further checks for this line
        }
        if (line && line.line_input_controls?.length) {
          for (let i = 0; i < line.line_input_controls?.length; i += 1) {
            if (line.line_input_controls[i]?.type === "Media" && line.line_input_controls[i]?.is_disabled != 1) {
              if (line?.line_input_controls[i]?.data?.filter((itm) => itm?.is_deleted !== 1)?.length > 0) {
                isLineInputIncomplete = true;
              }
            }
            // if (line.line_input_controls[i]?.type === "Information") {
            //   if (line.line_input_controls[i].data?.length > 0 && line.line_input_controls[i]?.data[0] !== '<p><br></p>' && line.line_input_controls[i]?.data !== '<p><br></p>') {
            //     isLineInputIncomplete = true;
            //   }
            // }
            if (line.line_input_controls[i]?.type === "Nameplate") {
              if (
                line?.line_input_controls[i]?.data?.modelNumber?.length > 0 ||
                line?.line_input_controls[i]?.data?.serialNumber?.length > 0 ||
                (line?.line_input_controls[i]?.data?.image !== null && line?.line_input_controls[i]?.data?.image !== undefined && line?.line_input_controls[i]?.data?.image.is_deleted !== 1)
              ) {
                isLineInputIncomplete = true;
              }
            }
            if (category.is_inspection_details !== 1) {
              if (line.line_input_controls[i]?.type === "Description") {
                if (line.line_input_controls[i].data?.length > 0 && line.line_input_controls[i]?.is_disabled != 1) {
                  isLineInputIncomplete = true;
                }
              }
              if (line.line_input_controls[i]?.type === "Additional Input") {
                if (line.line_input_controls[i].data?.length > 0) {
                  isLineInputIncomplete = true;
                }
              }
            }
            if (line.line_input_controls[i].type === "Location") {
              if (line.line_input_controls[i].data?.length > 0 && line.line_input_controls[i].data[0] !== null && line.line_input_controls[i]?.is_disabled != 1) {
                isLineInputIncomplete = true;
              }
            }
            if (line.line_input_controls[i].type === "CheckBox") {
              if (line.line_input_controls[i].data?.length > 0) {
                isLineInputIncomplete = true;
              }
            }

            if (line.line_input_controls[i].type === "Temperature") {
              if (line.line_input_controls[i].data?.value !== null && line.line_input_controls[i].data?.value !== "" && line.line_input_controls[i].data?.length !== 0) {
                isLineInputIncomplete = true;
              }
            }

            if (line.line_input_controls[i].type === "TextBox") {
              if (line.line_input_controls[i].data?.length > 0) {
                isLineInputIncomplete = true;
              }
            }
          }
        }
        if (line && line?.comments?.length) {
          line.comments?.forEach((itm) => {
            if (itm.is_selected === 1 && itm.is_deleted !== 1) {
              isLineInputIncomplete = true;
            }
            // if (itm.tags?.length > 0 && itm.is_deleted !== 1) {
            //   isLineInputIncomplete = true;
            // }
          });
        }
        if (isLineInputIncomplete) {
          lineComplete = lineComplete + 1;
        }
      });
    percentage = (lineComplete / totalLines) * 100;
    return { percentage, lineComplete, totalLines };
  }
};

export const checkCategoryPresent = (category, selected) => {
  let selectStatus = selected.map((selectedCat) => {
    if (selectedCat.uuid === category.uuid) return true;
    else return false;
  });
  return selectStatus.some((el) => el === true);
};

export const checkInspecLineComplete = (line) => {
  var checkInspecLineinput4Incomplete = false;
  if (line && line.line_input_controls.length) {
    for (let i = 0; i < line.line_input_controls.length; i += 1) {
      if (line.line_input_controls[i].type === "CheckBox") {
        if (line.line_input_controls[i].data?.length > 0) {
          checkInspecLineinput4Incomplete = true;
        }
      }
      if (line.line_input_controls[i].type === "Description") {
        if (line.line_input_controls[i].data?.length < 0) {
          checkInspecLineinput4Incomplete = true;
        }
      }
      if (line.line_input_controls[i].type === "Temperature") {
        if (line.line_input_controls[i].data?.value !== null && line.line_input_controls[i].data?.value !== "" && line.line_input_controls[i].data?.length !== 0) {
          checkInspecLineinput4Incomplete = true;
        }
      }
      if (line.line_input_controls[i].type === "TextBox") {
        if (line.line_input_controls[i].data?.length > 0) {
          checkInspecLineinput4Incomplete = true;
        }
      }
    }
  }
  return checkInspecLineinput4Incomplete;
};

export const checkLineComplete = (line, type) => {
  var isLineInputIncomplete = false;
  if (line && line?.line_input_controls?.length) {
    for (let i = 0; i < line.line_input_controls.length; i += 1) {
      if (line.line_input_controls[i].type === "Media") {
        if (line?.line_input_controls[i]?.data?.filter((itm) => itm?.is_deleted !== 1)?.length > 0) {
          isLineInputIncomplete = true;
        }
      }
      if (line.line_input_controls[i].type === "Nameplate") {
        if (
          line?.line_input_controls[i]?.data?.modelNumber?.length > 0 ||
          line?.line_input_controls[i]?.data?.serialNumber?.length > 0 ||
          (line?.line_input_controls[i]?.data?.image !== null && line?.line_input_controls[i]?.data?.image !== undefined && line?.line_input_controls[i]?.data?.image.is_deleted !== 1)
        ) {
          isLineInputIncomplete = true;
        }
      }
      if (type !== "inspectionDetails") {
        if (line.line_input_controls[i].type === "Description" && line.line_input_controls[i]?.is_disabled !== 1) {
          if (line.line_input_controls[i].data?.length > 0) {
            isLineInputIncomplete = true;
          }
        }
        if (line.line_input_controls[i].type === "Additional Input") {
          if (line.line_input_controls[i].data?.length > 0) {
            isLineInputIncomplete = true;
          }
        }
      }

      if (line.line_input_controls[i].type === "Location") {
        if (line.line_input_controls[i].data?.length > 0 && line.line_input_controls[i].data[0] !== null && line.line_input_controls[i]?.is_disabled !== 1) {
          isLineInputIncomplete = true;
        }
      }
      if (line.line_input_controls[i].type === "CheckBox") {
        if (line.line_input_controls[i].data?.length > 0) {
          isLineInputIncomplete = true;
        }
      }

      if (line.line_input_controls[i].type === "Temperature") {
        if (line.line_input_controls[i].data?.value !== null && line.line_input_controls[i].data?.value !== "" && line.line_input_controls[i].data?.length !== 0) {
          isLineInputIncomplete = true;
        }
      }

      if (line.line_input_controls[i].type === "TextBox") {
        if (line.line_input_controls[i].data?.length > 0) {
          isLineInputIncomplete = true;
        }
      }
    }
  }
  if (line && line?.comments?.length) {
    line?.comments?.forEach((itm) => {
      if (itm.is_selected === 1 && itm.is_deleted !== 1) {
        isLineInputIncomplete = true;
      }
      // if (itm.tags?.length > 0 && itm.is_deleted !== 1) {
      //   isLineInputIncomplete = true;
      // }
    });
  }
  if (line?.is_completed === true)
    isLineInputIncomplete = true;
  return isLineInputIncomplete;
};

// A soft-deleted comment (e.g. the source of a Move to) never counts towards its line's ratings/media
const isCommentCountedOnLine = (comment, isNarrative) =>
  comment?.is_deleted !== 1 && (isNarrative === true || comment?.is_selected === 1);

export const RatingCheck = (item, isNarrative, forLine, templateRatingData, category) => {
  let mediasIndexValue = item?.line_input_controls?.findIndex((itm) => itm.type === "Media");
  let commentArr = item && item.comments;
  var Rating = [];
  var imagesData = [];
  var videosData = [];
  if (!isNarrative) {
    if (forLine) {
      Rating = Rating.concat(resolveLineRatings(item, templateRatingData, category));
    } else if (commentArr && commentArr.length) {
      commentArr.forEach((commentItem) => {
        if (isCommentCountedOnLine(commentItem, isNarrative)) {
          // A comment that never had a rating picked still carries the template default, even when it
          // has no input controls at all (the rating counts in the summary already include it).
          Rating = Rating.concat(resolveCommentRatings(commentItem, templateRatingData, category));
          for (let i = 0; i < (commentItem.comment_input_controls?.length ?? 0); i += 1) {
            if (commentItem.comment_input_controls[i].type === "Media") {
              commentItem.comment_input_controls[i].data
                ?.filter((itm) => itm?.is_deleted !== 1)
                .forEach((item) => {
                  if (item.filetype === "image") {
                    imagesData.push(item);
                  } else if (item.filetype === "video") {
                    videosData.push(item);
                  }
                });
            }
          }
        }
      });
      // setRatings(Rating);
      // Several selected comments can share one rating (or the template default); show it once on the line strip.
      const seenRatingIds = new Set();
      Rating = Rating.filter((rating) => {
        const ratingKey = String(rating?.Id);
        if (seenRatingIds.has(ratingKey)) return false;
        seenRatingIds.add(ratingKey);
        return true;
      });
    }
  }

  if (mediasIndexValue >= 0) {
    let mediaData = item?.line_input_controls[mediasIndexValue]?.data;

    mediaData
      ?.filter((itm) => itm?.is_deleted !== 1)
      .forEach((item) => {
        if (item.filetype === "image") {
          imagesData.push(item);
        }
        if (item.filetype === "video") {
          videosData.push(item);
        }
      });
  }
  // setImages(imagesData);
  // setVideos(videosData);
  return Rating;
};


export const mediaCheck = (item, isNarrative) => {
  let mediasIndexValue = item?.line_input_controls?.findIndex((itm) => itm.type === "Media" && itm?.is_disabled != 1);
  let commentArr = item && item?.comments;
  let Rating = [];
  let imagesData = [];
  let videosData = [];
  if (commentArr && commentArr?.length !== 0) {
    commentArr.forEach((commentItem) => {
      if (commentItem?.comment_input_controls?.length && isCommentCountedOnLine(commentItem, isNarrative)) {
        for (let i = 0; i < commentItem.comment_input_controls.length; i += 1) {
          if (commentItem.comment_input_controls[i].type === "Rating") {
            let comData = commentItem.comment_input_controls[i].data;
            Rating = Rating.concat(comData);
          } else if (commentItem.comment_input_controls[i].type === "Media" && commentItem.comment_input_controls[i]?.is_disabled !== 1) {
            commentItem.comment_input_controls[i].data
              ?.filter((itm) => itm?.is_deleted !== 1)
              .forEach((item) => {
                if (item.filetype === "image") {
                  imagesData.push(item);
                } else if (item.filetype === "video") {
                  videosData.push(item);
                }
              });
          }
        }
      }
    });
    // setRatings(Rating);
  }

  if (mediasIndexValue >= 0) {
    let mediaData = item?.line_input_controls[mediasIndexValue]?.data;

    mediaData
      ?.filter((itm) => itm?.is_deleted !== 1)
      .forEach((item) => {
        if (item.filetype === "image") {
          imagesData.push(item);
        }
        if (item.filetype === "video") {
          videosData.push(item);
        }
      });
  }
  // setImages(imagesData);
  // setVideos(videosData);
  return imagesData;
};

// comment count
// Narrative comments have no selection checkbox, so every live comment on the line counts
export const narrativeCommentCount = (item, isNarrative) => {
  const comments = item?.comments?.filter((comment) => comment?.is_deleted !== 1) ?? [];
  return isNarrative ? comments : [];
};
// comment count

export const narrativeRatingCheck = (item, isNarrative) => {
  let commentArr = item && item?.comments;
  var Rating = [];
  if (isNarrative) {
    if (commentArr && commentArr?.length) {
      commentArr.forEach((commentItem) => {
        // Narrative comments have no selection checkbox, so every live comment's summaries count
        if (commentItem?.summaries?.length > 0 && commentItem.is_deleted !== 1) {
          let summaryData = commentItem.summaries;
          Rating = Rating?.concat(summaryData);
        }
      });
    }
  }
  return Rating;
};

export const videoCheck = (item, isNarrative) => {
  let mediasIndexValue = item?.line_input_controls?.findIndex((itm) => itm.type === "Media");

  let commentArr = item && item.comments;
  var Rating = [];
  var imagesData = [];
  var videosData = [];
  if (commentArr && commentArr?.length) {
    commentArr.forEach((commentItem) => {
      if (commentItem?.comment_input_controls?.length && isCommentCountedOnLine(commentItem, isNarrative)) {
        for (let i = 0; i < commentItem.comment_input_controls.length; i += 1) {
          if (commentItem.comment_input_controls[i].type === "Rating") {
            let comData = commentItem.comment_input_controls[i].data;
            Rating = Rating.concat(comData);
          } else if (commentItem.comment_input_controls[i].type === "Media") {
            commentItem.comment_input_controls[i].data
              ?.filter((itm) => itm?.is_deleted !== 1)
              .forEach((item) => {
                if (item.filetype === "image") {
                  imagesData.push(item);
                } else if (item.filetype === "video" && item.online === "online") {
                  videosData.push(item);
                }
              });
          }
        }
      }
    });
    // setRatings(Rating);
  }

  if (mediasIndexValue >= 0) {
    let mediaData = item?.line_input_controls[mediasIndexValue]?.data;

    mediaData
      ?.filter((itm) => itm?.is_deleted !== 1)
      .forEach((item) => {
        if (item.filetype === "image") {
          imagesData.push(item);
        }
        if (item.filetype === "video" && item.online === "online") {
          videosData.push(item);
        }
      });
  }
  // setImages(imagesData);
  // setVideos(videosData);
  return videosData;
};

// find selected comment counts
export const commentCount = (comments) => {
  let comntCount = 0;
  comments?.forEach((comment) => {
    comment.is_selected == 1 && comntCount++;
  });
  return comntCount;
};

// A comment shows in Review (comments, summary items and their counts) only when it is selected and not
// soft-deleted; a deleted comment can still carry is_selected from before it was deleted or moved.
export const isSelectedLiveComment = (comment) => comment?.is_deleted !== 1 && Boolean(comment?.is_selected);

export const separtesummeryCounter = (rating, template) => {
  const summaryName = rating
    ?.filter((item) => item?.include_in_summary == 1)
    ?.map((item) => {
      return { name: item?.Name, count: 0 };
    });
  // inspectionResponse?.map((template) =>
  template?.categories?.map((category) =>
    category?.lines?.map((lines) =>
      lines?.comments?.map((comment) => {
        if (isSelectedLiveComment(comment)) {
          resolveCommentRatings(comment, rating)?.map((el) => {
            summaryName.map((names, idx) => {
              if (el.Name == names.name) {
                summaryName[idx]["count"] = summaryName[idx].count + 1;
              }
            });
          });
        }
      })
    )
  );
  // );
  return JSON.stringify(summaryName);
};

export const summeryItemCount = (summeryRating, inspectionResponse) => {
  const summaryName = summeryRating.map((item) => {
    return { name: item?.Name, count: 0 };
  });
  inspectionResponse?.map((template) =>
    template?.categories?.map((category) =>
      category?.lines?.map((lines) =>
        lines?.comments?.map((comment) => {
          if (isSelectedLiveComment(comment)) {
            resolveCommentRatings(comment, template?.rating_data)?.map((el) => {
              summaryName.map((names, idx) => {
                if (el.Name == names.name) {
                  summaryName[idx]["count"] = summaryName[idx].count + 1;
                }
              });
            });
          }
        })
      )
    )
  );
  return JSON.stringify(summaryName);
};

export const totalCommentCounter = (inspectionResponse) => {
  let ratingcount = 0;
  let noRatingCount = noRatingCounter(inspectionResponse);
  inspectionResponse?.map((template) =>
    template?.categories?.map((category) =>
      category?.lines?.map((lines) =>
        lines?.comments?.map((comment) => {
          if (isSelectedLiveComment(comment)) {
            resolveCommentRatings(comment, template?.rating_data)?.length >= 1 && (ratingcount += 1);
          }
        })
      )
    )
  );
  return ratingcount + noRatingCount;
};

export const commentInputCounter = (rating, template) => {
  let commentCount = 0;
  // inspectionResponse?.map((template) =>
  template?.categories?.map((category) =>
    category?.lines?.map((lines) =>
      lines?.comments?.map((comment) => {
        if (isSelectedLiveComment(comment)) {
          resolveCommentRatings(comment, template?.rating_data)?.map((el) => el?.Name === rating?.Name && commentCount++);
        }
      })
    )
  );
  // );
  return commentCount;
};

export const noRatingCounter = (template, isNarrative) => {
  let noRatingCount = 0;
  let noRatingCountWithNoCommentInput = 0;
  if (!isNarrative) {
    template?.categories?.map((category) =>
      category?.lines?.map((lines) =>
        lines?.comments?.map((comment) => {
          if (isSelectedLiveComment(comment)) {
            // if(!comment?.comment_input_controls.length){noRatingCountWithNoCommentInput++}
            // A comment carrying only the template default rating still counts as rated.
            if (resolveCommentRatings(comment, template?.rating_data)?.length === 0) noRatingCount++;
          }
        })
      )
    );
  }
  return noRatingCount + noRatingCountWithNoCommentInput;
};

export function splitDate(date) {
  var result = [];
  var counter = 0;
  for (var i = date.length - 1; i >= 0; i -= 1) {
    result[counter] = date[i];
    counter += 1;
  }
  var date = result[1]?.replace(/-/g, "/");
  const tConvert = (time) => {
    // Check correct time format and split into components
    time = time.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [time];

    if (time.length > 1) {
      // If time format correct
      time = time.slice(1); // Remove full string match value
      time[5] = +time[0] < 12 ? " AM" : " PM"; // Set AM/PM
      time[0] = +time[0] % 12 || 12; // Adjust hours
    }
    return time.join(""); // return adjusted time or original string
  };
  var time = tConvert(result[0].slice(0, -3));
  return { date, time };
}
export const checkCategoryComplete = (category) => {
  if (category?.lines === null || category?.lines === undefined || category?.lines.length === 0) {
    return true;
  } else {
    let catStatusArray = category?.lines?.map((line) => checkLineComplete(line));
    return catStatusArray?.every((el) => el === true);
  }
};
export const checkTemplateComplete = (template) => {
  if (template?.categories === null || template?.categories === undefined || template?.categories?.length === 0) {
    return true;
  } else {
    let tempStatusArray = template?.categories?.filter((el) => el.is_inspection_details !== 1)?.map((category) => checkCategoryComplete(category));
    return tempStatusArray?.every((el) => el === true);
  }
};

export const categoryMediaCount = (category) => {
  let categoryImageCount = 0;
  let categoryVideoCount = 0;
  category?.introductory_text_media?.forEach((media) => {
    if (media.filetype === "image") {
      categoryImageCount++;
    } else if (media.filetype === "video") {
      categoryVideoCount++;
    }
  });
  category.lines?.forEach((line) => {
    let lineImageLength = mediaCheck(line)?.length;
    let lineVideoLength = videoCheck(line)?.length;
    categoryImageCount += lineImageLength;
    categoryVideoCount += lineVideoLength;
  });
  return { categoryImageCount, categoryVideoCount };
};

export const pdfMediaCount = (category) => {
  let categoryImageCount = 0;
  let categoryVideoCount = 0;
  categoryImageCount = category?.value?.filter((item) => item.filetype == "image" && item.is_deleted !== 1)?.length;
  categoryVideoCount = category?.value?.filter((item) => item.filetype == "video" && item.is_deleted !== 1)?.length;
  return { categoryImageCount, categoryVideoCount };
};

export const templateMediaCount = (template) => {
  let pdfCatgoryData = template?.pdf_data?.filter((category) => category.type === "MediaUploader");
  let templateImageCount = 0;
  let templateVideoCount = 0;
  if (template?.is_pdf_template !== 1) {
    template?.categories?.forEach((category) => {
      let { categoryImageCount, categoryVideoCount } = categoryMediaCount(category);
      templateImageCount += categoryImageCount;
      templateVideoCount += categoryVideoCount;
    });
  } else {
    pdfCatgoryData?.forEach((category) => {
      let { categoryImageCount, categoryVideoCount } = pdfMediaCount(category);
      templateImageCount += categoryImageCount;
      templateVideoCount += categoryVideoCount;
    });
  }
  return { templateImageCount, templateVideoCount };
};

export const totalMediaCount = (inspTemplates) => {
  let totalImageCount = 0;
  let totalVideoCount = 0;
  inspTemplates?.forEach((template) => {
    let { templateImageCount, templateVideoCount } = templateMediaCount(template);
    totalImageCount += templateImageCount;
    totalVideoCount += templateVideoCount;
  });
  return { totalImageCount, totalVideoCount };
};

export const templateMediaCheck = (template) => {
  let { templateImageCount, templateVideoCount } = templateMediaCount(template);
  if (templateImageCount + templateVideoCount === 0) return false;
  else return true;
};

export const categoryMediaCheck = (category, ...args) => {
  const [media_type] = args;
  if (category?.introductory_text_media?.length > 0) {
    return true;
  }
  let categoryMediaStatus = category?.lines?.map((line) => {
    if (media_type == "image" && mediaCheck(line)?.length > 0) {
      return true;
    } else if (media_type == "video" && videoCheck(line)?.length > 0) {
      return true;
    } else if (media_type == "all" && (mediaCheck(line)?.length > 0 || videoCheck(line)?.length > 0)) {
      return true;
    } else return false;
  });
  return categoryMediaStatus?.some((el) => el === true);
};

/**
 * New RML flow check.
 * When initiate-web-rw returns is_old_flow with a value of 0, report_media_library_files
 * already holds every media item that belongs in the Report Media Library, so the
 * inspectiontemplates tree (introductory_text_media, line/comment Media controls,
 * Nameplate images, pdf_data MediaUploader) does not have to be traversed.
 * Any other value, or a missing key, keeps the old traversal.
 */
export const isRmlOnlyFlow = (inspectionData) => {
  const isOldFlow = inspectionData?.is_old_flow;
  return isOldFlow === 0 || isOldFlow === "0" || isOldFlow === false;
};

export const getImageDimensions = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const imgWidth = img.width;
      const imgHeight = img.height;
      resolve({ imgWidth, imgHeight });
    };
    img.src = URL.createObjectURL(file);
  });
};

export const resizeImage = (file, width, height, quality) => {
  return new Promise((resolve, reject) => {
    new Compressor(file, {
      width,
      height,
      quality, // Set the image compression quality
      success(result) {
        // Resolves the Promise with the resized image data
        resolve(result);
      },
      error(err) {
        // Rejects the Promise with an error if there's an issue
        reject(err);
      },
    });
  });
};

export function getMergeIds(obj) {
  let mergeIds = [];
  function traverseElements(elements) {
    for (const element of elements) {
      if (element.merge_id) {
        mergeIds.push(element.merge_id);
      }
      if (element.elements && element.elements.length > 0) {
        traverseElements(element.elements);
      }
    }
  }

  if (obj.elements && obj.elements.length > 0) {
    traverseElements(obj.elements);
  }
  return mergeIds;
}

export async function addCheckFlagToPdfData(pdfData, mergeIdsArray, checked) {
  const updatedPdfData = await Promise.all(
    pdfData.map(async (item) => {
      const updatedAt = await GetUTCTime();
      const checkFlag = checked && mergeIdsArray.includes(item.merge_id);
      return {
        ...item,
        show_in_pdf: checkFlag ? 1 : 0,
        updated_at: updatedAt,
      };
    })
  );
  const filteredPdfData = updatedPdfData.filter((item) => mergeIdsArray.includes(item.merge_id));
  return filteredPdfData;
}
export const checkDescriptionDisabled = (line) => {
  let descriptionData = line?.line_input_controls?.filter((lineData) => lineData.type === "Description");
  if (descriptionData && descriptionData[0]&& descriptionData[0]?.is_disabled === 1) return true;
  else return false;
};

export function filterObjectsUptoLine(prevData, templateIndex, categoryIndex, lineIndex) {
  let filteredData = JSON.parse(JSON.stringify(prevData));
  let filteredTemplateObject = filteredData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject = filteredTemplateObject?.categories[categoryIndex];
  let filteredLineObject = filteredCategoryObject?.lines[lineIndex];
  filteredCategoryObject.lines = [filteredLineObject];
  filteredTemplateObject.categories = [filteredCategoryObject];
  filteredData.inspectiontemplates = [filteredTemplateObject];
  return filteredData;
}

export function filterObjectsUptoCategory(prevData, templateIndex, categoryIndex) {
  let filteredData = JSON.parse(JSON.stringify(prevData));
  let filteredTemplateObject = filteredData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject = filteredTemplateObject?.categories[categoryIndex];
  filteredTemplateObject.categories = [filteredCategoryObject];
  filteredData.inspectiontemplates = [filteredTemplateObject];
  return filteredData;
}
export function filterObjectsUptoTemplate(prevData, templateIndex) {
  let filteredData = JSON.parse(JSON.stringify(prevData));
  let filteredTemplateObject = filteredData?.inspectiontemplates[templateIndex];
  filteredData.inspectiontemplates = [filteredTemplateObject];
  return filteredData;
}

export function cleanPdfUptoTemplate(prevData, templateIndex) {
  let filteredData = JSON.parse(JSON.stringify(prevData));
  let filteredTemplateObject = filteredData?.inspectiontemplates[templateIndex];
  let payload = {
    guid: filteredData.guid,
    inspectiontemplates: [filteredTemplateObject]
  }
  return payload;
}

export function cleanPayloadForLocationData(prevData, templateIndex) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  delete filteredTemplateObject.categories;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnCategoryLevel(
  prevData,
  templateIndex,
  categoryIndex
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  delete filteredCategoryObject.lines;
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnCategoryLinesLevel(
  prevData,
  templateIndex,
  categoryIndex
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  if (Array.isArray(filteredCategoryObject?.lines)) {
    filteredCategoryObject.lines = filteredCategoryObject?.lines.map((line) => {
      const {
        line_input_controls,
        inspector_flagging_guids,
        comments,
        ...rest
      } = line;
      return rest;
    });
  }
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnCategoryAddLineLevel(
  prevData,
  templateIndex,
  categoryIndex
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnCategoryReorder(prevData, templateIndex) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];

  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  if (Array.isArray(filteredTemplateObject?.categories)) {
    filteredTemplateObject.categories = filteredTemplateObject?.categories.map(
      (category) => {
        const {
          lines,
          inspector_flagging_guids,
          introductory_text_media,
          ...rest
        } = category;
        return rest;
      }
    );
  }
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnLineLevel(
  prevData,
  templateIndex,
  categoryIndex,
  lineIndex
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  let filteredLineObject = filteredCategoryObject?.lines[lineIndex];
  delete filteredLineObject.comments;
  delete filteredLineObject.line_input_controls;
  delete filteredLineObject.inspector_flagging_guids;
  filteredCategoryObject.lines = [filteredLineObject];
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnLineReorder(
  prevData,
  templateIndex,
  categoryIndex,
  updatedAt
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];

  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  if (Array.isArray(filteredCategoryObject?.lines)) {
    filteredCategoryObject.lines = filteredCategoryObject?.lines.map((line) => {
      const {
        line_input_controls,
        inspector_flagging_guids,
        comments,
        ...rest
      } = line;
      rest.updated_at = updatedAt;
      return rest;
    });
  }
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnLineICLevel(
  prevData,
  templateIndex,
  categoryIndex,
  lineIndex,
  inputControlIndex
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  let filteredLineObject = filteredCategoryObject?.lines[lineIndex];
  let filteredLineICObject =
    filteredLineObject?.line_input_controls[inputControlIndex];
  filteredLineObject.line_input_controls = [filteredLineICObject];
  delete filteredLineObject.inspector_flagging_guids;
  delete filteredLineObject.comments;
  filteredCategoryObject.lines = [filteredLineObject];
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnLineICMediaLevel(
  prevData,
  templateIndex,
  categoryIndex,
  lineIndex,
  inputControlIndex,
  mediaIndex
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  let filteredLineObject = filteredCategoryObject?.lines[lineIndex];
  let filteredLineICObject =
    filteredLineObject?.line_input_controls[inputControlIndex];
  if (mediaIndex !== -1) {
    let filteredLineMediaObject = filteredLineICObject?.data[mediaIndex];
    filteredLineICObject.data = [filteredLineMediaObject];
  }
  filteredLineObject.line_input_controls = [filteredLineICObject];
  delete filteredLineObject.inspector_flagging_guids;
  delete filteredLineObject.comments;
  filteredCategoryObject.lines = [filteredLineObject];
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnCommentLevel(
  prevData,
  templateIndex,
  categoryIndex,
  lineIndex,
  commentIndex,
  event
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  let filteredLineObject = filteredCategoryObject?.lines[lineIndex];
  let filteredCommentObject = filteredLineObject?.comments[commentIndex];
  if (event == "CommentSelection") {
    delete filteredCommentObject.comment_input_controls;
  }
  filteredLineObject.comments = [filteredCommentObject];
  delete filteredLineObject.line_input_controls;
  delete filteredLineObject.inspector_flagging_guids;
  filteredCategoryObject.lines = [filteredLineObject];
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadForCommentReorder(
  prevData,
  templateIndex,
  categoryIndex,
  lineIndex,
  commentIndex,
  event
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  let filteredLineObject = filteredCategoryObject?.lines[lineIndex];
  delete filteredLineObject.line_input_controls;
  delete filteredLineObject.inspector_flagging_guids;
  filteredCategoryObject.lines = [filteredLineObject];
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}


export function cleanPayloadForCommentRelocation(prevData, touchedLines) {
  const payload = { guid: prevData.guid, inspectiontemplates: [] };
  touchedLines.forEach(({ template, category, line }) => {
    if (!template || !category || !line) return;
    const lineOnlyData = {
      guid: prevData.guid,
      inspectiontemplates: [{ ...template, categories: [{ ...category, lines: [line] }] }],
    };
    const [templatePayload] = cleanPayloadForCommentReorder(lineOnlyData, 0, 0, 0).inspectiontemplates;
    const [categoryPayload] = templatePayload.categories;
    const [linePayload] = categoryPayload.lines;

    const existingTemplate = payload.inspectiontemplates.find((item) => item.template_id === templatePayload.template_id);
    if (!existingTemplate) {
      payload.inspectiontemplates.push(templatePayload);
      return;
    }
    const existingCategory = existingTemplate.categories.find(
      (item) => item.uuid === categoryPayload.uuid && item.id === categoryPayload.id
    );
    if (!existingCategory) {
      existingTemplate.categories.push(categoryPayload);
      return;
    }
    if (!existingCategory.lines.some((item) => item.uuid === linePayload.uuid)) existingCategory.lines.push(linePayload);
  });
  return payload;
}

// Comments render sorted by `order` (see LineDetails visibleComments), so array position alone does not
// place a new comment. Insert it right after `sourceComment` in display order (or first when sourceComment
// is null) and renumber the non-deleted comments 1-based, matching the drag-reorder convention. The
// renumbered `order` values must be sent in the payload. Mutates `comments` and the comments in it.
export function insertCommentAfterInDisplayOrder(comments, sourceComment, newComment, updatedAt) {
  const commentsInDisplayOrder = comments
    .filter((comment) => comment?.is_deleted !== 1)
    .sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
  const displayInsertIndex = sourceComment ? commentsInDisplayOrder.indexOf(sourceComment) + 1 : 0;
  commentsInDisplayOrder.splice(displayInsertIndex, 0, newComment);
  commentsInDisplayOrder.forEach((comment, index) => {
    const displayOrder = index + 1;
    if (comment.order !== displayOrder) {
      comment.order = displayOrder;
      comment.updated_at = updatedAt;
    }
  });
  const arrayInsertIndex = sourceComment ? comments.indexOf(sourceComment) + 1 : 0;
  comments.splice(arrayInsertIndex, 0, newComment);
}

// Gives a duplicated/moved/copied comment's input controls fresh identities. `data` entries are objects for
// Rating and Media but plain strings for Location (e.g. ["North, South"]); spreading a string would turn it
// into a {0: "N", ...} object that renders as "[object Object]", so only object entries get new ids.
export function cloneCommentInputControls(commentInputControls, updatedAt) {
  if (!Array.isArray(commentInputControls)) return commentInputControls;
  return commentInputControls.map((control) => {
    const clonedControl = { ...control, uuid: uuidv4(), created_at: updatedAt, updated_at: updatedAt };
    if (Array.isArray(clonedControl.data)) {
      clonedControl.data = clonedControl.data.map((entry) =>
        entry !== null && typeof entry === "object"
          ? { ...entry, id: uuidv4(), created_at: updatedAt, updated_at: updatedAt }
          : entry
      );
    }
    return clonedControl;
  });
}

const COMMENT_TEXT_FIELDS = ["prompt", "recommendation", "title", "comment_text", "summary_comment_text"];
const COMMENT_DROPDOWN_FIELDS = ["comment_dropdowns", "summary_comment_dropdowns"];

// Dropdown and textbox placeholders render as inputs whose DOM id is the placeholder's id and are looked up
// with getElementById, so a cloned comment needs its own ids or opening/editing them hits the original's.
// Rewrites each dropdown_uuid (and its "[dropdown <uuid>]" placeholder) and each "[textbox id:<id>" id.
export function regenerateCommentPlaceholderIds(comment) {
  const newIds = new Map();
  const newIdFor = (oldId) => {
    if (!newIds.has(oldId)) newIds.set(oldId, uuidv4());
    return newIds.get(oldId);
  };
  COMMENT_DROPDOWN_FIELDS.forEach((field) => {
    if (!Array.isArray(comment[field])) return;
    comment[field] = comment[field].map((dropdown) =>
      dropdown?.dropdown_uuid ? { ...dropdown, dropdown_uuid: newIdFor(dropdown.dropdown_uuid) } : dropdown
    );
  });
  COMMENT_TEXT_FIELDS.forEach((field) => {
    if (typeof comment[field] !== "string") return;
    comment[field] = comment[field]
      .replace(/(\[?dropdown\s+)([a-f0-9-]+)(\])/gi, (match, prefix, dropdownId, suffix) =>
        newIds.has(dropdownId) ? `${prefix}${newIds.get(dropdownId)}${suffix}` : match
      )
      .replace(/(\[textbox\s+id:)([^;\]]+)/gi, (match, prefix, textboxId) => `${prefix}${newIdFor(textboxId.trim())}`);
  });
  return comment;
}

// UpdateTemplateDataSpecific only dispatches; the save API runs later in the saga. This resolves once the next
// save result lands in CommonReducer.updatedInspectionData (UPDATE_INSPECTION_RESPONSE stores the response,
// UPDATE_INSPECTION_ERROR stores the request Error). Call it before dispatching the save. A store subscription
// is used rather than component state so it still resolves if the calling component unmounts meanwhile.
export const waitForInspectionSaveResult = (store) =>
  new Promise((resolve) => {
    const resultBeforeSave = store.getState()?.CommonReducer?.updatedInspectionData;
    const unsubscribe = store.subscribe(() => {
      const saveResult = store.getState()?.CommonReducer?.updatedInspectionData;
      // "" is CLEAR_UPDATED_INSPECTION_DATA resetting the flag, not a save result
      if (saveResult === resultBeforeSave || saveResult === "" || saveResult === null || saveResult === undefined) return;
      unsubscribe();
      resolve({ succeeded: !(saveResult instanceof Error) });
    });
  });

// Soft-deletes a comment; a deleted comment must not stay selected, so a selected one is unselected too
export function markCommentDeleted(comment, updatedAt) {
  comment.is_deleted = 1;
  comment.updated_at = updatedAt;
  if (comment.is_selected === 1) {
    comment.is_selected = 0;
    comment.is_selected_updated_at = updatedAt;
  }
}

export const isTexasTemplate = (template) => isTruthyFlag(template?.is_texas);

const isNarrativeTemplate = (template) => isTruthyFlag(template?.is_narrative_template);

// Move to / Copy to targets: any template of the same kind, plus Texas <-> narrative (HomeTeam) in either
// direction, whose comments are converted between the two shapes. PDF form templates have no lines.
export const canRelocateCommentBetween = (sourceTemplate, destinationTemplate) => {
  if (isTruthyFlag(destinationTemplate?.is_pdf_template)) return false;
  if (isNarrativeTemplate(sourceTemplate) === isNarrativeTemplate(destinationTemplate)) return true;
  return isTexasTemplate(sourceTemplate) || isTexasTemplate(destinationTemplate);
};

// Standard and narrative comments hold the same content under different fields: prompt <-> title and
// recommendation <-> comment_text, with comment_dropdowns using the same [dropdown uuid] placeholders.
// The narrative summary (summary text, summaries) has no standard equivalent and is dropped; tags use the same
// [{ id, name, color }] shape on both sides and are kept.
const NARRATIVE_ONLY_COMMENT_FIELDS = ["title", "comment_text", "summary_comment_text", "summary_comment_dropdowns", "summaries"];
const STANDARD_ONLY_COMMENT_FIELDS = ["prompt", "recommendation", "comment_id", "defaultRating_id", "default_location", "is_mannual_prompt", "rating"];

const convertNarrativeCommentToStandard = (comment) => {
  const convertedComment = { ...comment, prompt: comment.title ?? "", recommendation: comment.comment_text ?? "" };
  NARRATIVE_ONLY_COMMENT_FIELDS.forEach((field) => delete convertedComment[field]);
  return convertedComment;
};

const convertStandardCommentToNarrative = (comment) => {
  const convertedComment = {
    ...comment,
    title: comment.prompt || comment.title || "",
    comment_text: comment.recommendation ?? "",
    summary_comment_text: "",
    summary_comment_dropdowns: [],
    summaries: [],
    tags: Array.isArray(comment.tags) ? comment.tags : [],
  };
  STANDARD_ONLY_COMMENT_FIELDS.forEach((field) => delete convertedComment[field]);
  return convertedComment;
};

const convertCommentForTemplate = (comment, sourceTemplate, destinationTemplate) => {
  const isNarrativeSource = isNarrativeTemplate(sourceTemplate);
  if (isNarrativeSource === isNarrativeTemplate(destinationTemplate)) return comment;
  return isNarrativeSource ? convertNarrativeCommentToStandard(comment) : convertStandardCommentToNarrative(comment);
};

// The comment that lands at a Move to / Copy to destination: new uuid, fresh input-control ids, converted to
// the destination template's comment shape. It arrives unselected in a standard template; narrative comments
// have no selection checkbox and only count/print when selected, so there it arrives selected. Texas (TREC)
// ratings must not carry over, so when the source or destination template is Texas the rating is emptied and
// removed_default_rating_id stops the destination's default rating being re-applied.
export function buildRelocatedComment(sourceComment, updatedAt, { sourceTemplate, destinationTemplate }) {
  const relocatedComment = regenerateCommentPlaceholderIds(
    convertCommentForTemplate(JSON.parse(JSON.stringify(sourceComment)), sourceTemplate, destinationTemplate)
  );
  relocatedComment.uuid = uuidv4();
  relocatedComment.created_at = updatedAt;
  relocatedComment.updated_at = updatedAt;
  relocatedComment.is_selected_updated_at = updatedAt;
  relocatedComment.is_deleted = 0;
  relocatedComment.is_selected = isNarrativeTemplate(destinationTemplate) ? 1 : 0;
  relocatedComment.comment_input_controls = cloneCommentInputControls(relocatedComment.comment_input_controls, updatedAt);
  if (isTexasTemplate(sourceTemplate) || isTexasTemplate(destinationTemplate)) {
    if (Array.isArray(relocatedComment.comment_input_controls)) {
      relocatedComment.comment_input_controls.forEach((control) => {
        if (control?.type === "Rating") control.data = [];
      });
    }
    const removedDefaultRatingId = isNarrativeTemplate(destinationTemplate)
      ? null
      : resolveRemovedDefaultRatingId([], destinationTemplate?.rating_data);
    if (removedDefaultRatingId === null) {
      delete relocatedComment.removed_default_rating_id;
    } else {
      relocatedComment.removed_default_rating_id = removedDefaultRatingId;
    }
  }
  return relocatedComment;
}

// Puts `comment` at the top of the destination line's comments (falling back to the category's first live
// line, then to the category itself) by giving it order 1 and renumbering the rest. Returns where it landed,
// or null when the destination category is gone.
export function addCommentToDestination(template, destination, comment, updatedAt) {
  const category = template?.categories?.find(
    (item) =>
      item?.is_deleted !== 1 &&
      ((destination?.categoryUuid && item.uuid === destination.categoryUuid) ||
        (destination?.categoryId && item.id === destination.categoryId))
  );
  if (!category) return null;
  const liveLines = (category.lines || []).filter((line) => line?.is_deleted !== 1);
  const line =
    liveLines.find(
      (item) =>
        (destination.lineUuid && item.uuid === destination.lineUuid) ||
        (destination.lineId && item.id === destination.lineId)
    ) ?? liveLines[0] ?? null;
  const commentOwner = line ?? category;
  commentOwner.comments = commentOwner.comments ?? [];
  insertCommentAfterInDisplayOrder(commentOwner.comments, null, comment, updatedAt);
  return { category, line };
}

export function cleanPayloadOnCommentICLevel(
  prevData,
  templateIndex,
  categoryIndex,
  lineIndex,
  commentIndex,
  inputControlIndex
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  let filteredLineObject = filteredCategoryObject?.lines[lineIndex];
  let filteredCommentObject = filteredLineObject?.comments[commentIndex];
  let filteredCommentICObject =
    filteredCommentObject?.comment_input_controls[inputControlIndex];
  filteredCommentObject.comment_input_controls = [filteredCommentICObject];
  filteredLineObject.comments = [filteredCommentObject];
  delete filteredLineObject.line_input_controls;
  delete filteredLineObject.inspector_flagging_guids;
  filteredCategoryObject.lines = [filteredLineObject];
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function cleanPayloadOnCommentICMediaLevel(
  prevData,
  templateIndex,
  categoryIndex,
  lineIndex,
  commentIndex,
  inputControlIndex,
  mediaIndex
) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  let filteredCategoryObject =
    filteredTemplateObject?.categories[categoryIndex];
  let filteredLineObject = filteredCategoryObject?.lines[lineIndex];
  let filteredCommentObject = filteredLineObject?.comments[commentIndex];
  let filteredCommentICObject =
    filteredCommentObject?.comment_input_controls[inputControlIndex];
  if (mediaIndex !== -1) {
    let filteredCommentMediaObject = filteredCommentICObject?.data[mediaIndex];
    filteredCommentICObject.data = [filteredCommentMediaObject];
  }
  filteredCommentObject.comment_input_controls = [filteredCommentICObject];
  filteredLineObject.comments = [filteredCommentObject];
  delete filteredLineObject.line_input_controls;
  delete filteredLineObject.inspector_flagging_guids;
  filteredCategoryObject.lines = [filteredLineObject];
  delete filteredCategoryObject.inspector_flagging_guids;
  delete filteredCategoryObject.introductory_text_media;
  filteredTemplateObject.categories = [filteredCategoryObject];
  delete filteredTemplateObject.location_data;
  delete filteredTemplateObject.rating_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

const mergePdfData = (original = [], updates = []) => {
  const updateKeys = new Set(
    updates.map((item) => `${item.merge_id}_${item.type}`)
  );

  const filteredOriginal = original.filter(
    (item) => !updateKeys.has(`${item.merge_id}_${item.type}`)
  );

  return [...filteredOriginal, ...updates];
};

export const mergePdfDataArray = async (original = [], updates = []) => {
  const updateKeys = new Set(
    updates.map((item) => `${item.merge_id}_${item.type}`)
  );
  const filteredOriginal = original.filter(
    (item) => !updateKeys.has(`${item.merge_id}_${item.type}`)
  );
  return [...filteredOriginal, ...updates];
};

export const processUpdatedInspectionState = (
  stateSection,
  payloadData,
  newPayload,
  inspectionDataForBulkMedia
) => {
  const updatedTemplates = stateSection?.data?.inspectiontemplates
    ?.filter((template) => {
      const updatedTemplate = newPayload?.find(
        (item) =>
          item?.template_id === template?.template_id &&
          item?.template_guid === template?.template_guid
      );

      // Keep template only if not marked as deleted
      return !updatedTemplate?.is_deleted;
    })
    ?.map((template) => {
      const updatedTemplate = newPayload?.find(
        (item) =>
          item?.template_id === template?.template_id &&
          item?.template_guid === template?.template_guid
      );

      if (!updatedTemplate) return template;

      if (template?.is_pdf_template) {
        const originalPdf = template?.pdf_data || [];
        const newPdf = updatedTemplate?.pdf_data || [];
        const mergedPdf = mergePdfData(originalPdf, newPdf);

        return {
          ...template,
          ...updatedTemplate,
          pdf_data: mergedPdf,
        };
      } else {
        let updatedCategories = template?.categories || [];

        if (updatedTemplate?.categories?.length) {
          updatedCategories = updatedCategories.map((category) => {
            const newCat = updatedTemplate?.categories?.find(
              (cat) => cat?.id === category?.id && cat?.uuid === category?.uuid
            );
            return newCat ? { ...category, ...newCat } : category;
          });

          const newCategories = updatedTemplate?.categories?.filter(
            (newCat) =>
              !updatedCategories?.some(
                (cat) => cat?.id === newCat?.id && cat?.uuid === newCat?.uuid
              )
          );

          updatedCategories = [...updatedCategories, ...newCategories]?.sort(
            (a, b) => {
              const aIsDetails = a?.is_inspection_details === 1 || a?.is_inspection_details === 2;
              const bIsDetails = b?.is_inspection_details === 1 || b?.is_inspection_details === 2;

              if (aIsDetails !== bIsDetails) {
                return aIsDetails ? -1 : 1;
              }

              const aDetailsFlag = a?.is_inspection_details ?? 0;
              const bDetailsFlag = b?.is_inspection_details ?? 0;
              if (aDetailsFlag !== bDetailsFlag) {
                return aDetailsFlag - bDetailsFlag;
              }

              return (a?.order ?? 0) - (b?.order ?? 0);
            }
          );
        }

        return {
          ...template,
          ...updatedTemplate,
          categories: updatedCategories,
        };
      }
    }
    );

  return {
    ...stateSection,
    data: {
      ...stateSection.data,
      inspectiontemplates: updatedTemplates,
      cover_photo: payloadData?.hasOwnProperty("cover_photo")
        ? payloadData.cover_photo
        : stateSection?.data?.cover_photo,
      attachments: payloadData?.hasOwnProperty("attachments")
        ? payloadData.attachments
        : stateSection?.data?.attachments,
      incomplete_line_input_controls: payloadData?.hasOwnProperty(
        "incomplete_line_input_controls"
      )
        ? payloadData.incomplete_line_input_controls
        : stateSection?.data?.incomplete_line_input_controls,
      completed_line_input_controls: payloadData?.hasOwnProperty(
        "completed_line_input_controls"
      )
        ? payloadData.completed_line_input_controls
        : stateSection?.data?.completed_line_input_controls,
      inspectorFlagList: payloadData?.hasOwnProperty("inspectorFlagList")
        ? payloadData.inspectorFlagList
        : stateSection?.data?.inspectorFlagList,
      report_media_library_files: (() => {
        const currentFiles = inspectionDataForBulkMedia?.data?.report_media_library_files || [];

        if (!payloadData?.hasOwnProperty("report_media_library_files")) {
          return currentFiles;
        }

        const payloadFiles = payloadData?.report_media_library_files;
        const map = new Map(currentFiles.map(item => [item.uuid, item]));

        payloadFiles.forEach(item => {
          map.set(item.uuid, item);
        });

        return Array.from(map.values());
      })(),
    },
  };
};

export const updatedCategoriesResponse = (stateSection, payload, response) => {
  let inspectionTemplates = [
    ...(stateSection?.data?.inspectiontemplates || []),
  ];

  inspectionTemplates = stateSection?.data?.inspectiontemplates?.map(
    (template) => {
      if (payload?.template_id !== template?.template_id) return template;

      return {
        ...template,
        categories: response?.data || [],
      };
    }
  );

  return {
    ...stateSection,
    data: {
      ...stateSection.data,
      inspectiontemplates: inspectionTemplates,
    },
  };
}

export const updatedCategoryLinesResponse = (stateSection, payload, response) => {
  let inspectionTemplates = [
    ...(stateSection?.data?.inspectiontemplates || []),
  ];

  inspectionTemplates = stateSection?.data?.inspectiontemplates?.map(
    (template) => {
      if (payload?.template_id !== template?.template_id) return template;

      const updatedTemplate = { ...template };

      updatedTemplate.categories = updatedTemplate?.categories?.map(
        (category) => {
          if (category?.uuid !== payload?.category_uuid) return category;

          return response?.data;
        }
      );
      return updatedTemplate;
    }
  );

  return {
    ...stateSection,
    data: {
      ...stateSection.data,
      inspectiontemplates: inspectionTemplates,
    },
  };
}

export const updateNarrativeComment = (stateSection, payload, response) => {
  const inspectionTemplates = (stateSection?.data?.inspectiontemplates || []).map(
    (template) => {
      if (template?.template_id !== payload?.payload?.template_id) return template;
      const updatedTemplate = { ...template };
      updatedTemplate.categories = (updatedTemplate?.categories || []).map((category) => {
        if (category?.uuid !== payload?.payload?.category_uuid) return category;
        const updatedCategory = { ...category };
        updatedCategory.lines = (updatedCategory?.lines || []).map((line) => {
          if (line?.uuid !== payload?.payload?.line_uuid) return line;
          return { ...line, comments: response?.data };
        });
        return updatedCategory;
      });
      updatedTemplate.rating_data = response?.updatedTemplateRatingData ?? template?.rating_data;
      return updatedTemplate;
    }
  );

  return {
    ...stateSection,
    data: {
      ...stateSection?.data,
      inspectiontemplates: inspectionTemplates
    },
  };
};


export const updateInspectionMediaData = (stateSection, payload, response) => {
  let inspectionTemplates = [
    ...(stateSection?.data?.inspectiontemplates || []),
  ];
  if (payload instanceof FormData) {
    payload = Object.fromEntries(payload.entries());
  }

  if (payload?.img_type !== "cover") {
    inspectionTemplates = stateSection?.data?.inspectiontemplates?.map(
      (template) => {
        if (payload?.template_id != template?.template_id) return template;

        const updatedTemplate = { ...template };

        if (updatedTemplate?.is_pdf_template) {
          const originalPdf = template?.pdf_data || [];
          const newPdf = response?.data || [];
          const mergedPdf = mergePdfData(originalPdf, [newPdf]);
          return {
            ...template,
            ...updatedTemplate,
            pdf_data: mergedPdf,
          };
        } else {
          updatedTemplate.categories = updatedTemplate?.categories?.map(
            (category) => {
              if (category?.uuid != payload?.category_id) return category;

              if (payload?.img_type == "introductory_text_media") {
                return {
                  ...category,
                  introductory_text_media: response?.data || [],
                };
              } else {
                const updatedCategory = { ...category };

                updatedCategory.lines = updatedCategory?.lines?.map((line) => {
                  if (line?.uuid != payload?.line_id) return line;

                  const updatedLine = { ...line };

                  if (
                    payload?.img_type == "details" ||
                    payload?.img_type == "nameplate"
                  ) {
                    let lineInputControls = [
                      ...(updatedLine?.line_input_controls || []),
                    ];
                    let existingIndex = lineInputControls.findIndex(
                      (input) =>
                        input?.type ==
                        (payload?.img_type == "nameplate"
                          ? "Nameplate"
                          : "Media")
                    );

                    if (existingIndex > -1) {
                      lineInputControls[existingIndex] = response?.data;
                    } else {
                      lineInputControls.push(response?.data);
                    }
                    updatedLine.line_input_controls = lineInputControls;
                  } else {
                    updatedLine.comments = (updatedLine?.comments || []).map(
                      (comment) => {
                        if (comment?.uuid != payload?.comment_id) return comment;
                        const updatedComment = { ...comment };
                        let commentInputControls = [
                          ...(updatedComment?.comment_input_controls || []),
                        ];
                        const existingIndex = commentInputControls.findIndex(
                          (input) => input?.type == "Media"
                        );
                        if (existingIndex > -1) {
                          commentInputControls[existingIndex] = response?.data;
                        } else {
                          commentInputControls.push(response?.data);
                        }
                        updatedComment.comment_input_controls =
                          commentInputControls;
                        return updatedComment;
                      }
                    );
                  }
                  return updatedLine;
                });
                return updatedCategory;
              }
            }
          );
          return updatedTemplate;
        }
      }
    );
  }
  return {
    ...stateSection,
    data: {
      ...stateSection.data,
      inspectiontemplates: inspectionTemplates,
      cover_photo:
        payload?.img_type === "cover"
          ? response?.data
          : stateSection?.data?.cover_photo,
    },
  };
};

export function filterPdfTemplateData(
  prevData,
  templateIndex,
  filteredPDFData,
  mergeId
) {
  let filteredData = JSON.parse(JSON.stringify(prevData));
  let filteredTemplateObject = filteredData?.inspectiontemplates[templateIndex];
  filteredTemplateObject.pdf_data = filteredPDFData;
  let pdfDataFilteredWithmergeID = filteredTemplateObject.pdf_data.filter((element) => element.merge_id === mergeId);
  filteredTemplateObject.pdf_data = pdfDataFilteredWithmergeID;
  filteredTemplateObject.pdf_categories = [];
  let payload = {
    guid: filteredData.guid,
    inspectiontemplates: [filteredTemplateObject],
  };
  return payload;
}
export const getNonNarrativeTemplatesGuid = (templates) => {
  let nonNarrativeTemplatesGuid = templates
    ?.map((template) => {
      if (template.is_narrative_template !== 1 && template.is_pdf_template !== 1) return template.template_guid;
    })
    .filter((item) => item !== undefined);
  return nonNarrativeTemplatesGuid;
};

export function convertFontToSpan(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString.innerHTML, "text/html");
  const sizeToPx = { 1: "10px", 2: "12px", 3: "14px", 4: "16px", 5: "18px", 6: "20px", 7: "22px" };
  function replaceFontWithSpan(element) {
    element.querySelectorAll("font").forEach((font) => {
      const fontSize = sizeToPx[font.getAttribute("size")] || "14px";
      const fontColor = font.getAttribute("color");
      const span = document.createElement("span");
      span.style.fontSize = fontSize;
      if (fontColor) span.style.color = fontColor;
      span.innerHTML = font.innerHTML;
      replaceFontWithSpan(span);
      font.replaceWith(span);
    });
  }
  replaceFontWithSpan(doc.body);
  return { innerHTML: doc.body.innerHTML };
}

export const allInspectorFlagging = (TemData) => {
  let totalLines = 0;
  let lineComplete = 0;
  let percentage = 0;
  const percentageCategoryList = [];
  const catwiseFlagListData = [];
  let inspectorflaggingData = [];

  const processLineInputControls = (lineInputControls) => {
    return lineInputControls.some((control) => {
      switch (control.type) {
        case "Media":
        case "Description":
        case "Location":
        case "CheckBox":
        case "TextBox":
          return control.data?.length > 0;
        case "Nameplate":
          return (
            control.data?.modelNumber?.length > 0 ||
            control.data?.serialNumber?.length > 0
          );
        case "Temperature":
          return control.data?.value?.length > 0;
        default:
          return false;
      }
    });
  };

  try {
    TemData?.forEach((template) => {
      if (template?.is_pdf_template !== 1) {
        template?.categories
          ?.filter((category) => category?.is_deleted !== 1)
          ?.forEach((category) => {
            totalLines = 0;
            lineComplete = 0;
            inspectorflaggingData.push(...(category?.inspector_flagging_guids || []));

            if (category.is_inspection_details === 2) {
              if (category.introductory_text) {
                totalLines = 1;
                lineComplete = 1;
              }
            } else {
              category.lines
                ?.filter((line) => line?.is_deleted !== 1)
                ?.forEach((line) => {
                  if (line?.inspector_flagging_guids) {
                    inspectorflaggingData.push(...line.inspector_flagging_guids);
                  }

                  totalLines += 1;
                  let isComplete = false;

                  if (line.line_input_controls?.length) {
                    isComplete = processLineInputControls(line.line_input_controls);
                  }

                  if (line.comments?.some((comment) => comment.is_selected === 1)) {
                    isComplete = true;
                  }

                  if (isComplete) {
                    lineComplete += 1;
                  }
                });
            }

            percentage = (lineComplete / totalLines) * 100;
            percentageCategoryList.push({
              category_id: category.uuid,
              percentage,
            });
            catwiseFlagListData.push({
              category_id: category.uuid,
              inspector_flagging_guids: inspectorflaggingData,
            });

            inspectorflaggingData = [];
          });
      } else {
        try {
          template?.pdf_categories
            ?.filter((category) => category?.is_deleted !== 1)
            ?.forEach((category) => {
              inspectorflaggingData.push(...(category?.inspector_flagging_guids || []));
              catwiseFlagListData.push({
                category_id: category.uuid,
                inspector_flagging_guids: inspectorflaggingData,
              });
              inspectorflaggingData = [];
            });
        } catch (error) {
          console.error("Error processing PDF categories:", error);
        }
      }
    });
  } catch (error) {
    console.error("Error processing templates:", error);
  }

  return {
    percentageCategoryList,
    catwiseFlagListData,
  };
};

export function cleanPayloadForTemplate(prevData, templateIndex) {
  let inspectionData = JSON.parse(JSON.stringify(prevData));
  let filterData = {
    guid: inspectionData.guid,
  };
  let filteredTemplateObject =
    inspectionData?.inspectiontemplates[templateIndex];
  delete filteredTemplateObject.categories;
  delete filteredTemplateObject.rating_data;
  delete filteredTemplateObject.location_data;
  filterData.inspectiontemplates = [filteredTemplateObject];
  return filterData;
}

export function getLuminance(hexColor) {
  const r = parseInt(hexColor?.substr(1, 2), 16) / 255;
  const g = parseInt(hexColor?.substr(3, 2), 16) / 255;
  const b = parseInt(hexColor?.substr(5, 2), 16) / 255;
  const [R, G, B] = [r, g, b].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * R + 0.7152 * G + 0.0722 * B;
}

export const getTextColor = (bgColor) => {
  const luminance = bgColor ? getLuminance(bgColor) : "#ffffff";
  return luminance > 0.179 ? "#000000" : "#ffffff";
};